import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useTranslation } from '../t';
import defaultPicture from '../assets/default.jpg';
import C from '../colors';

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12"/>
    </svg>
  );
}

function ChevronIcon({ dir }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      {dir === 'left'
        ? <path d="M15 18l-6-6 6-6"/>
        : <path d="M9 18l6-6-6-6"/>}
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 13 4 4L19 7"/>
    </svg>
  );
}

function ImageSlide({ src, alt }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { setLoaded(false); }, [src]);

  return (
    <div style={{ flexShrink: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
      {/* Skeleton */}
      {!loaded && (
        <div style={{
          position: 'absolute',
          width: '60%', maxWidth: 640, height: '70%',
          borderRadius: 8,
          background: 'linear-gradient(90deg, rgba(255,255,255,.04) 25%, rgba(255,255,255,.09) 50%, rgba(255,255,255,.04) 75%)',
          backgroundSize: '200% 100%',
          animation: 'imgShimmer 1.4s infinite',
        }}/>
      )}
      {src && (
        <img
          src={src}
          alt={alt}
          onLoad={() => setLoaded(true)}
          style={{
            maxWidth: '100%', maxHeight: '100%',
            objectFit: 'contain',
            userSelect: 'none', pointerEvents: 'none',
            opacity: loaded ? 1 : 0,
            transition: 'opacity .3s ease',
          }}
        />
      )}
    </div>
  );
}

const ImageDialog = inject('snackbarStore')(observer(({ pictureStore, handlePictureSelect, snackbarStore }) => {
  const { currentPic, selectedPictures } = pictureStore;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animated, setAnimated] = useState(false);
  const { t } = useTranslation();

  const handleClose = () => {
    pictureStore.setCurrentPic(null);
    setAnimated(false);
  };

  const handleNext = useCallback(() => {
    const last = pictureStore.previewPictures.length;
    if (currentIndex < last - 1) {
      setCurrentIndex(i => i + 1);
    } else {
      snackbarStore.showSnackbar('已经是最后一张了', 'warning');
    }
  }, [pictureStore.previewPictures, currentIndex, snackbarStore]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(i => i - 1);
    } else {
      snackbarStore.showSnackbar('已经是第一张了', 'warning');
    }
  }, [currentIndex, snackbarStore]);

  const handleToggleSelect = () => {
    const pic = pictureStore.previewPictures[currentIndex];
    if (pic) handlePictureSelect(pic.id, !pic.claimed);
  };

  useEffect(() => {
    if (!currentPic) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  { handlePrev(); e.preventDefault(); }
      if (e.key === 'ArrowRight') { handleNext(); e.preventDefault(); }
      if (e.key === 'Escape')     { handleClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlePrev, handleNext, currentPic]);

  useEffect(() => {
    if (!currentPic) return;
    const idx = pictureStore.previewPictures.findIndex(p => p.id === currentPic.id);
    setCurrentIndex(idx >= 0 ? idx : 0);
    setAnimated(true);
  }, [currentPic]);

  const handlers = useSwipeable({ onSwipedLeft: handleNext, onSwipedRight: handlePrev });

  if (!currentPic) return null;

  const pictures = pictureStore.previewPictures;
  const pic = pictures[currentIndex];
  const isClaimed = pic?.claimed;
  const total = pictures.length;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(10,8,9,.95)',
        display: 'flex', flexDirection: 'column',
      }}
      {...handlers}
    >
      {/* Top bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px',
        background: 'linear-gradient(to bottom, rgba(0,0,0,.6), transparent)',
      }}>
        <button onClick={handleClose} style={{
          width: 38, height: 38, borderRadius: 10,
          background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)',
          color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center',
        }}>
          <CloseIcon/>
        </button>

        {/* Counter */}
        <span style={{
          fontFamily: C.mono, fontSize: 12, color: 'rgba(255,255,255,.6)',
          letterSpacing: 1, fontVariantNumeric: 'tabular-nums',
        }}>
          {currentIndex + 1} / {total}
        </span>

        {/* Select toggle */}
        {handlePictureSelect ? (
          <button onClick={handleToggleSelect} style={{
            height: 38, padding: '0 16px', borderRadius: 10,
            background: isClaimed ? C.brand : 'rgba(255,255,255,.1)',
            border: `1px solid ${isClaimed ? C.brand : 'rgba(255,255,255,.15)'}`,
            color: '#fff', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 7,
            fontFamily: C.sans, fontSize: 13, fontWeight: 500,
            transition: 'background .15s',
          }}>
            <CheckIcon/>
            {isClaimed ? '已选' : '选择'}
          </button>
        ) : (
          <div style={{ width: 38 }}/>
        )}
      </div>

      {/* Image strip */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <div style={{
          display: 'flex', height: '100%',
          transition: animated ? 'transform .25s ease' : 'none',
          transform: `translateX(-${currentIndex * 100}%)`,
          willChange: 'transform',
        }}>
          {pictures.map((p, i) => (
            <ImageSlide
              key={p.id}
              src={Math.abs(i - currentIndex) <= 1 ? (p.publicUrl || defaultPicture) : null}
              alt={p.fileName}
            />
          ))}
        </div>

        {/* Prev button */}
        {currentIndex > 0 && (
          <button onClick={handlePrev} style={{
            position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
            width: 44, height: 44, borderRadius: 12,
            background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)',
            color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center',
            backdropFilter: 'blur(8px)',
          }}>
            <ChevronIcon dir="left"/>
          </button>
        )}

        {/* Next button */}
        {currentIndex < total - 1 && (
          <button onClick={handleNext} style={{
            position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
            width: 44, height: 44, borderRadius: 12,
            background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)',
            color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center',
            backdropFilter: 'blur(8px)',
          }}>
            <ChevronIcon dir="right"/>
          </button>
        )}
      </div>

      {/* Bottom bar: filename */}
      {pic?.fileName && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '20px 24px 20px',
          background: 'linear-gradient(to top, rgba(0,0,0,.7), transparent)',
          textAlign: 'center',
        }}>
          <span style={{
            fontFamily: C.mono, fontSize: 11, color: 'rgba(255,255,255,.5)',
            letterSpacing: 0.5,
          }}>
            {pic.fileName}
          </span>
        </div>
      )}
    </div>
  );
}));

export default ImageDialog;
